import React, { useState, useCallback } from 'react';
import { glossaryData } from '../data/glossary';
import Knob from './Knob';
import DialLabel from './DialLabel';
import GlossaryScreen from './GlossaryScreen';
import SafeIcon from '../common/SafeIcon';

export default function MainInterface() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragAngle, setDragAngle] = useState(glossaryData[0].angle);

  const activeItem = glossaryData[activeIndex];
  const leftItems = glossaryData.filter(item => item.side === 'left');
  const rightItems = glossaryData.filter(item => item.side === 'right');

  // Handle live updates while dragging to update the screen content
  const handleAngleChange = useCallback((angle) => {
    setDragAngle(angle);
    
    // Find closest item while dragging to show preview in screen
    const closest = glossaryData.reduce((prev, curr) => {
      return (Math.abs(curr.angle - angle) < Math.abs(prev.angle - angle) ? curr : prev);
    });
    
    const newIndex = glossaryData.findIndex(item => item.id === closest.id);
    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  }, [activeIndex]);

  // Handle snapping to the final position on drag end
  const handleSnap = useCallback((finalAngle) => {
    const closest = glossaryData.reduce((prev, curr) => {
      return (Math.abs(curr.angle - finalAngle) < Math.abs(prev.angle - finalAngle) ? curr : prev);
    });
    
    setActiveIndex(glossaryData.findIndex(item => item.id === closest.id));
    setDragAngle(closest.angle);
  }, []);

  const selectItemManually = (index) => {
    setActiveIndex(index);
    setDragAngle(glossaryData[index].angle);
  };

  return (
    <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4 font-sans text-gray-900">
      
      {/* Main Machine Panel */}
      <div className="w-full max-w-6xl relative p-6 md:p-12 rounded-[3rem] shadow-[0_40px_80px_rgba(0,0,0,0.8),_inset_0_2px_10px_rgba(255,255,255,0.3)] overflow-hidden">
        
        {/* Brushed Metal Texture Background */}
        <div className="absolute inset-0 bg-[#bdc3c7]" style={{
          backgroundImage: `
            linear-gradient(90deg, rgba(255,255,255,0.2) 0%, rgba(0,0,0,0.05) 10%, rgba(255,255,255,0.3) 25%, rgba(0,0,0,0.02) 40%, rgba(255,255,255,0.4) 50%, rgba(0,0,0,0.02) 60%, rgba(255,255,255,0.3) 75%, rgba(0,0,0,0.05) 90%, rgba(255,255,255,0.2) 100%),
            linear-gradient(0deg, rgba(0,0,0,0.1) 0%, transparent 10%, transparent 90%, rgba(255,255,255,0.4) 100%)
          `
        }} />

        {/* Top Branding / Title */}
        <div className="relative z-10 flex justify-between items-start mb-8 md:mb-12 px-4">
          <div className="flex flex-col items-center gap-1 opacity-60">
            <SafeIcon name="Cpu" className="text-4xl text-gray-800" />
            <span className="text-[10px] font-black tracking-[0.3em] text-gray-800">LabourLawTeach</span>
          </div>
          <div className="text-right">
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-gray-800 italic" style={{
              textShadow: '0 1px 0 white',
              color: 'rgb(31, 41, 55)'
            }}>Trasparenzatrice</h1>
            <h2 style={{ color: 'rgb(185, 28, 28)' }} className="text-xs font-bold text-red-700 tracking-[0.2em] uppercase mt-1">
              Mod. UE2023/970 Vers. IT96/2026
            </h2>
            <div className="h-1 w-24 bg-red-700 ml-auto mt-2 rounded-full opacity-60" />
          </div>
        </div>

        {/* Dial Section */}
        <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-4 max-w-6xl mx-auto px-4">
          
          {/* Left Labels */}
          <div className="w-full lg:w-1/3 flex flex-col gap-4 lg:gap-6 pr-0 lg:pr-16 relative">
            <div className="hidden lg:block absolute -top-8 left-0 text-[10px] font-black text-red-800/60 tracking-widest uppercase">
              SEZIONE ALPHA
            </div>
            {leftItems.map((item) => (
              <DialLabel 
                key={item.id} 
                item={item} 
                isActive={activeItem.id === item.id}
                onClick={() => selectItemManually(glossaryData.findIndex(i => i.id === item.id))}
              />
            ))}
          </div>

          {/* Central Knob (Draggable) */}
          <div className="w-full lg:w-1/3 flex flex-col items-center justify-center py-4 lg:py-0 relative z-20">
            <Knob 
              currentAngle={dragAngle} 
              onAngleChange={handleAngleChange}
              onSnap={handleSnap}
            />
            <div className="mt-6 flex flex-col items-center">
              <div className="w-12 h-1 bg-gray-400/30 rounded-full mb-2" />
              <p className="text-[10px] font-bold text-gray-600 tracking-[0.4em] uppercase">Seleziona la funzione</p>
            </div>
          </div>

          {/* Right Labels */}
          <div className="w-full lg:w-1/3 flex flex-col gap-4 lg:gap-6 pl-0 lg:pl-16 relative">
             <div className="hidden lg:block absolute -top-8 right-0 text-[10px] font-black text-gray-600 tracking-widest uppercase">
              SEZIONE BETA
            </div>
            {rightItems.map((item) => (
              <DialLabel 
                key={item.id} 
                item={item} 
                isActive={activeItem.id === item.id}
                onClick={() => selectItemManually(glossaryData.findIndex(i => i.id === item.id))}
              />
            ))}
          </div>

        </div>

        {/* Digital Screen Section */}
        <div className="relative z-10 mt-12 mb-8">
          <GlossaryScreen activeItem={activeItem} />
        </div>

      </div>
    </div>
  );
}